/**
 * Methods build on the source code of next-intl/middleware/utils.tsx
 * All necessary types for MiddlewareConfigWithDefaults are copied since for whatever reason they cant be imported
 */

export type LocalePrefix = "as-needed" | "always" | "never"
export type Pathnames<Locales extends AllLocales> = Record<
  string,
  | {
      [Key in Locales[number]]: string
    }
  | string
>
type RoutingBaseConfig<Locales extends AllLocales> = {
  /** A list of all locales that are supported. */
  locales: Locales
  defaultLocale: Locales[number]
  /** The default locale can be used without a prefix (e.g. `/about`). If you prefer to have a prefix for the default locale as well (e.g. `/en/about`), you can switch this option to `always`.
   */
  localePrefix?: LocalePrefix
}
export type DomainConfig<Locales extends AllLocales> = Omit<RoutingBaseConfig<Locales>, "locales" | "localePrefix"> & {
  /** The domain name (e.g. "example.com", "www.example.com" or "fr.example.com"). Note that the `x-forwarded-host` or alternatively the `host` header will be used to determine the requested domain. */
  domain: string
  /** The locales availabe on this particular domain. */
  locales?: RoutingBaseConfig<Locales[number][]>["locales"]
}
type MiddlewareConfig<Locales extends AllLocales> = RoutingBaseConfig<Locales> & {
  /** Can be used to change the locale handling per domain. */
  domains?: DomainConfig<Locales>[]
  /** Sets the `Link` response header to notify search engines about content in other languages (defaults to `true`). See https://developers.google.com/search/docs/specialty/international/localized-versions#http */
  alternateLinks?: boolean
  /** By setting this to `false`, the cookie as well as the `accept-language` header will no longer be used for locale detection. */
  localeDetection?: boolean
  /** Maps internal pathnames to external ones which can be localized per locale. */
  pathnames?: Pathnames<Locales>
}
export type MiddlewareConfigWithDefaults<Locales extends AllLocales> = MiddlewareConfig<Locales> & {
  alternateLinks: boolean
  localePrefix: LocalePrefix
  localeDetection: boolean
}

type AllLocales = readonly string[]

export function matchesPathname(
  /** E.g. `/users/[userId]-[userName]` */
  template: string,
  /** E.g. `/users/23-jane` */
  pathname: string,
) {
  const regex = templateToRegex(template)
  return regex.test(pathname)
}

export function templateToRegex(template: string): RegExp {
  const regexPattern = template
    // Replace optional catchall ('[[...slug]]')
    .replace(/\[\[(\.\.\.[^\]]+)\]\]/g, "?(.*)")
    // Replace catchall ('[...slug]')
    .replace(/\[(\.\.\.[^\]]+)\]/g, "(.+)")
    // Replace regular parameter ('[slug]')
    .replace(/\[([^\]]+)\]/g, "([^/]+)")

  return new RegExp(`^${regexPattern}$`)
}

export function getFirstPathnameSegment(pathname: string) {
  return pathname.split("/")[1]
}

function isOptionalCatchAllSegment(pathname: string) {
  return pathname.includes("[[...")
}

function isCatchAllSegment(pathname: string) {
  return pathname.includes("[...")
}

function isDynamicSegment(pathname: string) {
  return pathname.includes("[")
}

export function comparePathnamePairs(a: string, b: string): number {
  const pathA = a.split("/")
  const pathB = b.split("/")

  const maxLength = Math.max(pathA.length, pathB.length)
  for (let i = 0; i < maxLength; i++) {
    const segmentA = pathA[i]
    const segmentB = pathB[i]

    // If one of the paths ends, prioritize the shorter path
    if (!segmentA && segmentB) return -1
    if (segmentA && !segmentB) return 1

    // Prioritize static segments over dynamic segments
    // @ts-ignore
    if (!isDynamicSegment(segmentA) && isDynamicSegment(segmentB)) return -1
    // @ts-ignore
    if (isDynamicSegment(segmentA) && !isDynamicSegment(segmentB)) return 1

    // Prioritize non-catch-all segments over catch-all segments
    // @ts-ignore
    if (!isCatchAllSegment(segmentA) && isCatchAllSegment(segmentB)) return -1
    // @ts-ignore
    if (isCatchAllSegment(segmentA) && !isCatchAllSegment(segmentB)) return 1

    // Prioritize non-optional catch-all segments over optional catch-all segments
    // @ts-ignore
    if (!isOptionalCatchAllSegment(segmentA) && isOptionalCatchAllSegment(segmentB)) {
      return -1
    }
    // @ts-ignore
    if (isOptionalCatchAllSegment(segmentA) && !isOptionalCatchAllSegment(segmentB)) {
      return 1
    }

    if (segmentA === segmentB) continue
  }

  // Both pathnames are completely static
  return 0
}

export function getSortedPathnames(pathnames: string[]) {
  const sortedPathnames = pathnames.sort(comparePathnamePairs)
  return sortedPathnames
}

/**
 * Returns [resolvedTemplateLocale, internalTemplateName] - the locale is the locale of the pathName and the templateName is the key inside the pathnames object
 */
export function getInternalTemplate<Locales extends AllLocales, Pathnames extends NonNullable<MiddlewareConfigWithDefaults<Locales>["pathnames"]>>(
  pathnames: Pathnames,
  pathname: string,
  locale: Locales[number],
): [Locales[number] | undefined, keyof Pathnames | undefined] {
  const sortedPathNames = getSortedPathnames(Object.keys(pathnames))
  return getInternalTemplateWithSortedPathnames(pathnames, sortedPathNames, pathname, locale)
}

export function getInternalTemplateWithSortedPathnames<Locales extends AllLocales, Pathnames extends NonNullable<MiddlewareConfigWithDefaults<Locales>["pathnames"]>>(
  pathnames: Pathnames,
  sortedPathNames: string[],
  pathname: string,
  locale: Locales[number],
): [Locales[number] | undefined, keyof Pathnames | undefined] {
  // Try to find a localized pathname that matches
  for (const internalPathname of sortedPathNames) {
    const localizedPathnamesOrPathname = pathnames[internalPathname]
    if (typeof localizedPathnamesOrPathname === "string") {
      const localizedPathname = localizedPathnamesOrPathname
      if (matchesPathname(localizedPathname, pathname)) {
        return [undefined, internalPathname]
      }
    } else {
      // Prefer the entry with the current locale in case multiple
      // localized pathnames match the current pathname
      // @ts-ignore
      const sortedEntries = Object.entries(localizedPathnamesOrPathname)
      const curLocaleIndex = sortedEntries.findIndex(([entryLocale]) => entryLocale === locale)
      if (curLocaleIndex > 0) {
        // @ts-ignore
        sortedEntries.unshift(sortedEntries.splice(curLocaleIndex, 1)[0])
      }

      for (const [entryLocale, entryPathname] of sortedEntries) {
        if (matchesPathname(entryPathname as string, pathname)) {
          return [entryLocale, internalPathname]
        }
      }
    }
  }

  // Try to find an internal pathname that matches (this can be the case
  // if all localized pathnames are different from the internal pathnames).
  for (const internalPathname of Object.keys(pathnames)) {
    if (matchesPathname(internalPathname, pathname)) {
      return [undefined, internalPathname]
    }
  }

  // No match
  return [undefined, undefined]
}

export function formatTemplatePathname(sourcePathname: string, sourceTemplate: string, targetTemplate: string, localePrefix?: string) {
  const params = getRouteParams(sourceTemplate, sourcePathname)
  let targetPathname = ""
  if (localePrefix) {
    targetPathname = `/${localePrefix}`
  }

  targetPathname += formatPathname(targetTemplate, params)
  targetPathname = normalizeTrailingSlash(targetPathname)

  return targetPathname
}

/**
 * Removes potential locales from the pathname and remove a trailing slash, only supports cleaning of supportedLocales
 */
export function getNormalizedPathname<Locales extends AllLocales>(pathname: string, supportedLocales: Locales) {
  // Add trailing slash for consistent handling
  // both for the root as well as nested paths
  if (!pathname.endsWith("/")) {
    pathname += "/"
  }

  const match = new RegExp(`^/(${supportedLocales.join("|")})/(.*)`, "i").exec(pathname)
  let result = match ? "/" + match[2] : pathname

  if (result !== "/") {
    result = normalizeTrailingSlash(result)
  }

  return result
}

export function findCaseInsensitiveLocale<Locales extends AllLocales>(candidate: string, locales: Locales) {
  return locales.find(locale => locale.toLowerCase() === candidate.toLowerCase())
}

export function getPathnameLocale<Locales extends AllLocales>(pathname: string, locales: Locales): Locales[number] | undefined {
  const pathLocaleCandidate = getFirstPathnameSegment(pathname)
  // @ts-ignore
  const pathLocale = findCaseInsensitiveLocale(pathLocaleCandidate, locales) ? pathLocaleCandidate : undefined
  return pathLocale
}

export function getRouteParams(template: string, pathname: string) {
  const regex = templateToRegex(template)
  const match = regex.exec(pathname)
  if (!match) return undefined
  const params: Record<string, string> = {}
  for (let i = 1; i < match.length; i++) {
    // @ts-ignore
    const key = template.match(/\[([^\]]+)\]/g)?.[i - 1].replace(/[[\]]/g, "")
    if (key) {
      // @ts-ignore
      params[key] = match[i]
    }
  }
  return params
}

export function formatPathname(template: string, params?: object) {
  if (!params) return template

  // Simplify syntax for optional catchall ('[[...slug]]') so
  // we can replace the value with simple interpolation
  template = template.replace(/\[\[/g, "[").replace(/\]\]/g, "]")

  let result = template
  Object.entries(params).forEach(([key, value]) => {
    result = result.replace(`[${key}]`, value)
  })

  return result
}

export function getPathWithSearch(pathname: string, search: string | undefined) {
  let pathWithSearch = pathname
  if (search) {
    pathWithSearch += search
  }
  return pathWithSearch
}

export function applyBasePath(pathname: string, basePath: string) {
  return normalizeTrailingSlash(normalizeTrailingSlash(basePath) + pathname)
}

export function normalizeTrailingSlash(pathname: string) {
  if (pathname.endsWith("/")) {
    pathname = pathname.slice(0, -1)
  }
  return pathname
}
